import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import useAdd from "../../../api/useAdd";
import useUpdate from "../../../api/useUpdate";
import useDelete from "../../../api/useDelete";
import StageGroupFormDialog from "./StageGroupFormDialog";
import StageGroupMembersDialog from "./StageGroupMembersDialog";
import StageRotationFormDialog from "./StageRotationFormDialog";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, PersonAdd as PersonAddIcon } from "@mui/icons-material";

export default function StageRotationList() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [rotationDialogOpen, setRotationDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForMembers, setGroupForMembers] = useState(null);
  const [editingRotation, setEditingRotation] = useState(null);

  const { data: groupsData, isLoading: groupsLoading } = useFetchObjects(
    ["stage-groups"],
    "stage-groups/?page_size=200",
    token
  );
  const groups = groupsData?.results ?? groupsData ?? [];

  const { data: departmentsData } = useFetchObjects(["departments-all"], "departments/?page_size=500", token);
  const { data: usersData } = useFetchObjects(["users-stage"], "users/?page_size=300", token);
  const departments = departmentsData?.results ?? departmentsData ?? [];
  const users = usersData?.results ?? usersData ?? [];

  const listUrl = departmentFilter
    ? `stage-rotations/?page=${page}&page_size=${rowsPerPage}&department=${departmentFilter}&ordering=group,sequence`
    : `stage-rotations/?page=${page}&page_size=${rowsPerPage}&ordering=group,sequence,start_date`;
  const { data: rotationsData, isLoading: rotationsLoading, isError, error } = useFetchObjects(
    ["stage-rotations", page, rowsPerPage, departmentFilter],
    listUrl,
    token
  );
  const rotations = Array.isArray(rotationsData) ? rotationsData : rotationsData?.results || [];
  const totalRotations = Array.isArray(rotationsData) ? rotationsData.length : rotationsData?.count || 0;

  const { handleAdd: addGroup } = useAdd("stage-groups", token, t("academic.groupAdded", "Group added."));
  const { handleUpdate: updateGroup } = useUpdate("stage-groups", token);
  const { handleDelete: deleteGroup, ConfirmDialog: ConfirmDeleteGroup } = useDelete("stage-groups", token);

  const { handleAdd: addRotation } = useAdd("stage-rotations", token, t("academic.stageRotationAdded", "Stage rotation added."));
  const { handleUpdate: updateRotation } = useUpdate("stage-rotations", token);
  const { handleDelete: deleteRotation, ConfirmDialog: ConfirmDeleteRotation } = useDelete("stage-rotations", token);

  const handleOpenAddGroup = () => { setEditingGroup(null); setGroupDialogOpen(true); };
  const handleOpenEditGroup = (row) => { setEditingGroup(row); setGroupDialogOpen(true); };
  const handleCloseGroupDialog = () => { setGroupDialogOpen(false); setEditingGroup(null); };
  const handleSaveGroup = (payload) => {
    if (editingGroup) updateGroup(editingGroup.id, payload);
    else addGroup(payload);
    queryClient.invalidateQueries({ queryKey: ["stage-groups"] });
    handleCloseGroupDialog();
  };

  const handleOpenAddMembers = (group) => { setGroupForMembers(group); setMembersDialogOpen(true); };
  const handleCloseMembersDialog = () => { setMembersDialogOpen(false); setGroupForMembers(null); };
  const handleMembersAdded = () => {
    queryClient.invalidateQueries({ queryKey: ["stage-groups"] });
    queryClient.invalidateQueries({ queryKey: ["stage-group-members"] });
  };

  const handleOpenAddRotation = () => { setEditingRotation(null); setRotationDialogOpen(true); };
  const handleOpenEditRotation = (row) => { setEditingRotation(row); setRotationDialogOpen(true); };
  const handleCloseRotationDialog = () => { setRotationDialogOpen(false); setEditingRotation(null); };
  const handleSaveRotation = (payload) => {
    if (editingRotation) updateRotation(editingRotation.id, payload);
    else addRotation(payload);
    queryClient.invalidateQueries({ queryKey: ["stage-rotations"] });
    handleCloseRotationDialog();
  };

  if (isError) return <Alert severity="error" sx={{ m: 2 }}>{error?.message || "Failed to load"}</Alert>;

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ConfirmDeleteGroup />
      <ConfirmDeleteRotation />
      <Paper sx={{ p: 3, width: "100%" }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>{t("academic.year7Stage", "Year 7 (Stage)")}</Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tab label={t("academic.groups", "Groups")} />
          <Tab label={t("academic.assignToDepartments", "Assign to departments")} />
        </Tabs>

        {tab === 0 && (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddGroup}>{t("academic.addGroup", "Add group")}</Button>
            </Box>
            {groupsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 400 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.200" }}>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>{t("academic.groupName", "Group name")}</strong></TableCell>
                      <TableCell><strong>{t("academic.groupCode", "Code")}</strong></TableCell>
                      <TableCell><strong>{t("academic.memberCount", "Members")}</strong></TableCell>
                      <TableCell align="right"><strong>{t("common.actions", "Actions")}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>{t("academic.noGroupsYet", "No groups yet. Create a group, then add members and assign the group to departments.")}</TableCell></TableRow>
                    ) : (
                      groups.map((row, idx) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{row.name || "—"}</TableCell>
                          <TableCell>{row.code || "—"}</TableCell>
                          <TableCell>{row.member_count ?? "—"}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleOpenAddMembers(row)} title={t("academic.addMembers", "Add members")}><PersonAddIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleOpenEditGroup(row)} title={t("common.edit", "Edit")}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => deleteGroup(row.id)} color="error" title={t("common.delete", "Delete")}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>{t("academic.filterByDepartment", "Filter by department")}</InputLabel>
                <Select value={departmentFilter} label={t("academic.filterByDepartment", "Filter by department")} onChange={(e) => { setDepartmentFilter(e.target.value); setPage(1); }}>
                  <MenuItem value="">{t("common.all", "All")}</MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddRotation} disabled={groups.length === 0}>{t("academic.assignGroupToDepartment", "Assign group to department")}</Button>
            </Box>
            {rotationsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
            ) : (
              <>
                <TableContainer sx={{ width: "100%", overflow: "auto" }}>
                  <Table size="small" sx={{ minWidth: 500 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.200" }}>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>{t("academic.group", "Group")}</strong></TableCell>
                        <TableCell><strong>{t("academic.department", "Department")}</strong></TableCell>
                        <TableCell><strong>{t("academic.sequence", "Sequence")}</strong></TableCell>
                        <TableCell><strong>{t("academic.startDate", "Start date")}</strong></TableCell>
                        <TableCell><strong>{t("academic.endDate", "End date")}</strong></TableCell>
                        <TableCell align="right"><strong>{t("common.actions", "Actions")}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rotations.length === 0 ? (
                        <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow>
                      ) : (
                        rotations.map((row, idx) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                            <TableCell>{row.group_display || row.academic_display || "—"}</TableCell>
                            <TableCell>{row.department_display || "—"}</TableCell>
                            <TableCell>{row.sequence ?? "—"}</TableCell>
                            <TableCell>{row.start_date || "—"}</TableCell>
                            <TableCell>{row.end_date || "—"}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => handleOpenEditRotation(row)} title={t("common.edit", "Edit")} disabled={!row.group}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => deleteRotation(row.id)} color="error" title={t("common.delete", "Delete")}><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination component="div" count={totalRotations} page={page - 1} onPageChange={(e, p) => setPage(p + 1)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }} rowsPerPageOptions={[10, 25, 50]} />
              </>
            )}
          </>
        )}
      </Paper>

      <StageGroupFormDialog open={groupDialogOpen} onClose={handleCloseGroupDialog} group={editingGroup} onSave={handleSaveGroup} />
      <StageGroupMembersDialog open={membersDialogOpen} onClose={handleCloseMembersDialog} group={groupForMembers} token={token} onAdded={handleMembersAdded} />
      <StageRotationFormDialog open={rotationDialogOpen} onClose={handleCloseRotationDialog} rotation={editingRotation} groups={groups} departments={departments} users={users} onSave={handleSaveRotation} />
    </Box>
  );
}
